import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { LISTING_CATEGORY_LABELS, formatUsdcBaseUnits } from "@hybrid-escrow/shared";
import { apiClient } from "../lib/api-client";
import { queryKeys } from "../lib/queryClient";
import { POLL } from "../lib/polling";
import { Money } from "../components/Money";
import { StarRatingDisplay } from "../components/StarRating";
import { SellerBadges } from "../components/SellerBadges";
import { EmptyState, ErrorBanner, LoadingState, describeError } from "../components/States";
import { usePageMeta } from "../lib/seo";

export function WatchlistPage() {
  usePageMeta({
    title: "Your watchlist",
    description: "Listings and saved searches you're tracking.",
    path: "/watchlist",
    index: false,
  });
  const queryClient = useQueryClient();

  const watchlist = useQuery({
    queryKey: queryKeys.watchlist,
    queryFn: ({ signal }) => apiClient.getWatchlist({}, signal),
    refetchInterval: POLL.catalogue,
  });
  const savedSearches = useQuery({
    queryKey: queryKeys.savedSearches,
    queryFn: ({ signal }) => apiClient.getSavedSearches({}, signal),
  });

  const unwatch = useMutation({
    mutationFn: (listingId: string) => apiClient.unwatchListing(listingId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.watchlist }),
  });
  const deleteSearch = useMutation({
    mutationFn: (id: string) => apiClient.deleteSavedSearch(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.savedSearches }),
  });

  return (
    <div className="stack">
      <h1>Your watchlist</h1>

      <section className="stack">
        <h2>Watching for a price drop</h2>
        {watchlist.isLoading && <LoadingState label="Loading watchlist…" />}
        {watchlist.isError && (
          <ErrorBanner error={watchlist.error} onRetry={() => void watchlist.refetch()} />
        )}
        {watchlist.data && watchlist.data.items.length === 0 && (
          <EmptyState title="Not watching anything yet">
            <p>
              Open a <Link to="/">listing</Link> and click "Add to watchlist" to get emailed if its
              price drops.
            </p>
          </EmptyState>
        )}
        {watchlist.data && watchlist.data.items.length > 0 && (
          <div className="grid grid--listings">
            {watchlist.data.items.map((entry) => (
              <div key={entry.id} className="listing-card">
                <span className="badge badge--neutral text-sm">
                  {LISTING_CATEGORY_LABELS[entry.listing.category]}
                </span>
                <Link to={`/listings/${entry.listing.id}`}>
                  <h3>{entry.listing.title}</h3>
                </Link>
                <StarRatingDisplay rating={entry.listing.sellerRating} size="sm" />
                <SellerBadges badges={entry.listing.sellerBadges} />
                <Money baseUnits={entry.listing.priceBaseUnits} />
                {BigInt(entry.listing.priceBaseUnits) < BigInt(entry.lastNotifiedPriceBaseUnits) && (
                  <p className="text-sm" style={{ color: "var(--color-success, green)" }}>
                    Price dropped since you started watching!
                  </p>
                )}
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  disabled={unwatch.isPending}
                  onClick={() => unwatch.mutate(entry.listing.id)}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}
        {unwatch.isError && (
          <p className="field-error-text" role="alert">
            {describeError(unwatch.error)}
          </p>
        )}
      </section>

      <section className="stack">
        <h2>Saved searches</h2>
        <p className="text-sm muted">
          Save a search from the <Link to="/">browse page</Link> to get emailed when a new
          matching listing goes live.
        </p>
        {savedSearches.isLoading && <LoadingState label="Loading saved searches…" />}
        {savedSearches.isError && (
          <ErrorBanner error={savedSearches.error} onRetry={() => void savedSearches.refetch()} />
        )}
        {savedSearches.data && savedSearches.data.items.length > 0 && (
          <div className="stack" style={{ gap: "var(--space-2)" }}>
            {savedSearches.data.items.map((search) => (
              <div key={search.id} className="card row row--between">
                <div>
                  <strong>{search.label}</strong>
                  <p className="text-sm muted">
                    {[
                      search.q && `keyword "${search.q}"`,
                      search.category && LISTING_CATEGORY_LABELS[search.category],
                      search.maxPriceBaseUnits &&
                        `under $${formatUsdcBaseUnits(BigInt(search.maxPriceBaseUnits))}`,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </p>
                </div>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  disabled={deleteSearch.isPending}
                  onClick={() => deleteSearch.mutate(search.id)}
                >
                  Delete
                </button>
              </div>
            ))}
          </div>
        )}
        {deleteSearch.isError && (
          <p className="field-error-text" role="alert">
            {describeError(deleteSearch.error)}
          </p>
        )}
      </section>
    </div>
  );
}
